import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, Check, Scissors, User, Calendar, Clock, BadgeCheck, MessageCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_971b0a5e-2a58-4bd4-a5ef-b384a7e4c355/artifacts/qroeuqc4_D09061CF-773C-466E-9659-A1C4CF9EC193.png";

const STEPS = ["Serviço", "Barbeiro", "Data & Hora", "Seus dados"];
const EASE = [0.22, 1, 0.36, 1];

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const maxISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
};

const stepVariants = {
  enter: { x: 60, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -60, opacity: 0 },
};

export default function Booking() {
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [service, setService] = useState(null);
  const [barber, setBarber] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [refFile, setRefFile] = useState(null);
  const [refPreview, setRefPreview] = useState(null);

  useEffect(() => {
    Promise.all([axios.get(`${API}/services`), axios.get(`${API}/barbers`)])
      .then(([s, b]) => { setServices(s.data); setBarbers(b.data); })
      .catch(() => toast.error("Não foi possível carregar os serviços. Recarregue a página."));
  }, []);

  useEffect(() => {
    if (!date || !barber || !service) return;
    setLoadingSlots(true);
    setTime("");
    axios.get(`${API}/slots`, { params: { date, barber: barber.id, service: service.id } })
      .then((r) => { setSlots(r.data.slots); setClosed(r.data.closed); })
      .catch(() => toast.error("Erro ao carregar horários."))
      .finally(() => setLoadingSlots(false));
  }, [date, barber, service]);

  const canNext = useMemo(() => {
    if (step === 0) return !!service;
    if (step === 1) return !!barber;
    if (step === 2) return !!date && !!time;
    if (step === 3) return name.trim().length >= 2 && whatsapp.replace(/\D/g, "").length >= 10;
    return false;
  }, [step, service, barber, date, time, name, whatsapp]);

  const submit = async () => {
    setSubmitting(true);
    try {
      let referenceId = null;
      if (refFile) {
        const fd = new FormData();
        fd.append("file", refFile);
        const up = await axios.post(`${API}/references`, fd);
        referenceId = up.data.id;
      }
      const { data } = await axios.post(`${API}/appointments`, {
        service_id: service.id,
        barber_id: barber.id,
        date,
        time,
        name: name.trim(),
        whatsapp,
        reference_id: referenceId,
      });
      setDone(data);
      toast.success("Agendamento realizado!");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao agendar. Tente novamente.");
      if (e.response?.status === 409) setStep(2);
    } finally {
      setSubmitting(false);
    }
  };

  const waText = done
    ? encodeURIComponent(`Olá! Agendei ${done.service_name} com ${done.barber_name} dia ${fmtDate(done.date)} às ${done.time}. Código: ${done.code}`)
    : "";

  return (
    <div className="bg-ink min-h-screen" data-testid="booking-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="booking-home-link">
            <img src={LOGO} alt="Estúdio New Era" className="h-9 w-9 md:h-11 md:w-11 rounded-full object-cover ring-1 ring-gold/50" />
            <span className="font-bebas text-lg md:text-xl tracking-widest text-cream">Estúdio <span className="text-gold">New Era</span></span>
          </Link>
          <Link to="/" className="focus-gold flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-smoke hover:text-gold transition-colors" data-testid="booking-back-home">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-20">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} data-testid="booking-confirmation">
            <div className="border border-gold/40 bg-surface p-8 md:p-12 text-center">
              <BadgeCheck className="w-14 h-14 text-gold mx-auto mb-6" />
              <p className="font-playfair italic text-gold text-2xl md:text-3xl">agendado</p>
              <h2 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl mt-2">Tá marcado!</h2>
              <div className="mt-8 inline-block border border-white/10 px-8 py-6 text-left space-y-2">
                <p className="text-sm text-smoke">Código</p>
                <p className="font-bebas text-4xl text-gold tracking-widest" data-testid="booking-code">{done.code}</p>
                <p className="text-sm text-cream pt-3">{done.service_name} · R$ {done.price}</p>
                <p className="text-sm text-cream">com {done.barber_name}</p>
                <p className="text-sm text-cream capitalize">{fmtDate(done.date)} às {done.time}</p>
                <p className="text-sm text-smoke pt-2">Status: <span className="text-green-400">confirmado automaticamente</span></p>
              </div>
              <p className="mt-8 text-sm text-smoke max-w-md mx-auto">
                Chegue com 5 minutos de antecedência. Se precisar remarcar, chama a gente no WhatsApp.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <a
                  href={`https://wa.me/5547997755448?text=${waText}`}
                  target="_blank" rel="noreferrer"
                  data-testid="confirmation-whatsapp-button"
                  className="focus-gold inline-flex items-center gap-2 bg-gold text-ink font-bebas text-xl tracking-widest px-8 py-3 hover:bg-golddark transition-colors"
                >
                  <MessageCircle className="w-5 h-5" /> Avisar no WhatsApp
                </a>
                <Link to="/" data-testid="confirmation-home-button" className="focus-gold inline-flex items-center gap-2 border border-white/20 text-cream font-bebas text-xl tracking-widest px-8 py-3 hover:border-gold hover:text-gold transition-colors">
                  Voltar ao início
                </Link>
                <Link to="/consulta" data-testid="confirmation-lookup-link" className="focus-gold inline-flex items-center gap-2 border border-white/20 text-cream font-bebas text-xl tracking-widest px-8 py-3 hover:border-gold hover:text-gold transition-colors">
                  Consultar ou cancelar
                </Link>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
              <p className="font-playfair italic text-gold text-2xl md:text-3xl">reserve seu horário</p>
              <h1 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-7xl mt-1">Agendamento</h1>
            </motion.div>

            <div className="mt-10 flex items-center gap-2" data-testid="booking-progress">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1 transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-white/10"}`} />
                  <p className={`mt-2 text-[10px] md:text-xs uppercase tracking-[0.2em] ${i <= step ? "text-gold" : "text-smoke"}`}>
                    {i + 1}. {s}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 min-h-[380px]">
              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div key="s0" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: EASE }}>
                    <h2 className="font-bebas text-2xl tracking-widest text-smoke uppercase mb-6 flex items-center gap-3">
                      <Scissors className="w-5 h-5 text-gold" /> Escolha o serviço
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {services.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          data-testid={`service-option-${s.id}`}
                          onClick={() => setService(s)}
                          className={`focus-gold text-left border p-6 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                            service?.id === s.id ? "border-gold bg-gold/10" : "border-white/10 bg-surface hover:border-gold/50"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <p className="font-bebas text-2xl tracking-wide text-cream leading-tight">{s.name}</p>
                            {service?.id === s.id && <Check className="w-5 h-5 text-gold shrink-0" />}
                          </div>
                          <p className="font-bebas text-2xl text-gold mt-3">R$ {s.price}</p>
                          <p className="text-xs text-smoke mt-1">{s.duration} min</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 1 && (
                  <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: EASE }}>
                    <h2 className="font-bebas text-2xl tracking-widest text-smoke uppercase mb-6 flex items-center gap-3">
                      <User className="w-5 h-5 text-gold" /> Escolha o barbeiro
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {barbers.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          data-testid={`barber-option-${b.id}`}
                          onClick={() => setBarber(b)}
                          className={`focus-gold text-left border p-8 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                            barber?.id === b.id ? "border-gold bg-gold/10" : "border-white/10 bg-surface hover:border-gold/50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="w-16 h-16 rounded-full border border-gold/50 overflow-hidden flex items-center justify-center bg-ink">
                              {b.photo ? (
                                <img src={`${BACKEND}/api/barbers/${b.id}/photo`} alt={b.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="font-bebas text-3xl text-gold">{b.name[0]}</span>
                              )}
                            </div>
                            {barber?.id === b.id && <Check className="w-5 h-5 text-gold" />}
                          </div>
                          <p className="font-bebas text-3xl tracking-wide text-cream mt-5">{b.name}</p>
                          <p className="text-xs text-smoke mt-1 uppercase tracking-[0.2em]">Barbeiro</p>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: EASE }}>
                    <h2 className="font-bebas text-2xl tracking-widest text-smoke uppercase mb-6 flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-gold" /> Escolha data e horário
                    </h2>
                    <input
                      type="date"
                      value={date}
                      min={todayISO()}
                      max={maxISO()}
                      onChange={(e) => setDate(e.target.value)}
                      data-testid="booking-date-input"
                      className="focus-gold w-full sm:w-72 bg-surface border border-white/15 text-cream font-manrope px-5 py-4 text-base outline-none [color-scheme:dark]"
                    />
                    {date && (
                      <div className="mt-8">
                        <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-4 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gold" /> Horários de {fmtDate(date)} · com {barber?.name}
                        </p>
                        {loadingSlots ? (
                          <p className="text-sm text-smoke" data-testid="slots-loading">Carregando horários...</p>
                        ) : closed ? (
                          <p className="text-sm text-eraorange" data-testid="slots-closed">Fechado neste dia. Aos domingos o estúdio não abre.</p>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="slots-grid">
                            {slots.map((s) => (
                              <button
                                key={s.time}
                                type="button"
                                disabled={!s.available}
                                onClick={() => setTime(s.time)}
                                data-testid={`slot-${s.time.replace(":", "")}`}
                                className={`focus-gold font-bebas text-xl tracking-widest py-2.5 border transition-colors duration-200 ${
                                  !s.available
                                    ? "border-white/5 text-smoke/40 line-through cursor-not-allowed"
                                    : time === s.time
                                      ? "bg-gold text-ink border-gold"
                                      : "border-white/15 text-cream hover:border-gold"
                                }`}
                              >
                                {s.time}
                              </button>
                            ))}
                          </div>
                        )}
                        {!loadingSlots && !closed && slots.length > 0 && slots.every((s) => !s.available) && (
                          <p className="text-sm text-eraorange mt-4" data-testid="slots-full">Dia lotado com este barbeiro. Tente outra data ou outro barbeiro.</p>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: EASE }}>
                    <h2 className="font-bebas text-2xl tracking-widest text-smoke uppercase mb-6 flex items-center gap-3">
                      <User className="w-5 h-5 text-gold" /> Seus dados
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                      <div>
                        <label htmlFor="booking-name" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Seu nome</label>
                        <input
                          id="booking-name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Ex.: João Silva"
                          data-testid="booking-name-input"
                          className="focus-gold w-full bg-surface border border-white/15 text-cream font-manrope px-5 py-4 text-base outline-none placeholder:text-smoke/50"
                        />
                      </div>
                      <div>
                        <label htmlFor="booking-whatsapp" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Seu WhatsApp</label>
                        <input
                          id="booking-whatsapp"
                          type="tel"
                          value={whatsapp}
                          onChange={(e) => setWhatsapp(e.target.value)}
                          placeholder="(47) 99999-9999"
                          data-testid="booking-whatsapp-input"
                          className="focus-gold w-full bg-surface border border-white/15 text-cream font-manrope px-5 py-4 text-base outline-none placeholder:text-smoke/50"
                        />
                      </div>
                    </div>
                    <div className="mt-6 max-w-2xl">
                      <label htmlFor="booking-reference" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Foto de referência (opcional)</label>
                      <p className="text-xs text-smoke mb-3">Tem um corte de referência? Manda a foto que o barbeiro já chega sabendo o que você quer.</p>
                      <input
                        id="booking-reference"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setRefFile(f);
                          setRefPreview(f ? URL.createObjectURL(f) : null);
                        }}
                        data-testid="booking-reference-input"
                        className="focus-gold w-full bg-surface border border-white/15 text-cream font-manrope px-4 py-3 text-sm outline-none file:mr-4 file:bg-gold file:text-ink file:border-0 file:font-bebas file:tracking-widest file:px-4 file:py-1.5 file:cursor-pointer"
                      />
                      {refPreview && (
                        <img src={refPreview} alt="Referência escolhida" data-testid="booking-reference-preview" className="mt-3 w-24 h-24 object-cover border border-gold/40" />
                      )}
                    </div>
                    <div className="mt-8 border border-white/10 p-6 max-w-2xl" data-testid="booking-summary">
                      <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-3">Resumo</p>
                      <p className="font-bebas text-3xl text-cream">{service?.name} <span className="text-gold">· R$ {service?.price}</span></p>
                      <p className="text-sm text-smoke mt-1 capitalize">com {barber?.name} · {fmtDate(date)} às {time}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-12 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="booking-back-button"
                className="focus-gold font-bebas text-xl tracking-widest px-8 py-3 border border-white/15 text-smoke hover:text-cream hover:border-white/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Voltar
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => canNext && setStep((s) => s + 1)}
                  disabled={!canNext}
                  data-testid="booking-next-button"
                  className="focus-gold group inline-flex items-center gap-2 bg-gold text-ink font-bebas text-xl tracking-widest px-10 py-3 hover:bg-golddark transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Avançar <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canNext || submitting}
                  data-testid="booking-submit-button"
                  className="focus-gold inline-flex items-center gap-2 bg-gold text-ink font-bebas text-xl tracking-widest px-10 py-3 hover:bg-golddark transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {submitting ? "Agendando..." : "Confirmar agendamento"}
                </button>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
